import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { apiError, formatAmount, formatDay, todayIso } from "../../lib/finance";
import { ADVANCE_KINDS, ADVANCE_METHODS, CURRENCIES, advancePayload, advanceProblem, emptyAdvance } from "../../lib/claims";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Banknote, Loader2, Plus, RefreshCw, Undo2 } from "lucide-react";

const selectClass = "h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-base md:text-sm";

// Cash handed to someone BEFORE a mission (« Avance remise ») and what they hand back (« Restitution »). Never an expense:
// it only changes what Jamora still owes that person. Not to be confused with « Remboursement » (Jamora paying back a note de frais).
export function AdvanceDialog({ open, onOpenChange, onRecorded, mission = null, person = null, kind = "given", held = null, currency: fixedCurrency = null }) {
  const [form, setForm] = useState(emptyAdvance());
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState([]);
  const [picked, setPicked] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const key = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyAdvance(fixedCurrency || "XOF"), kind, collaborator_id: person?.id || "", ...(held ? { amount: held } : {}) });
    setPicked(person); setQuery(""); setPeople([]); setShowErrors(false);
    key.current = crypto.randomUUID(); // one key per opening: a double click cannot record it twice
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when the dialog opens

  useEffect(() => {
    if (!open || picked) return undefined;
    const handle = setTimeout(() => {
      api.get("/finance/payer-candidates", { params: { q: query.trim() || undefined } }).then(({ data }) => setPeople(data.items)).catch(() => setPeople([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, query, picked]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const problem = advanceProblem(form, { held: kind === "returned" ? held : null });
  const returning = form.kind === "returned";

  const save = async () => {
    setShowErrors(true);
    if (problem) return;
    setSaving(true);
    try {
      await api.post("/finance/advances", advancePayload(form, mission?.id), { headers: { "Idempotency-Key": key.current } });
      toast.success(returning ? "Restitution enregistrée" : `Avance remise à ${picked?.display_name || "la personne"}`);
      onRecorded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="advance-dialog">
        <DialogHeader>
          <DialogTitle>{returning ? "Enregistrer une restitution" : "Remettre une avance"}</DialogTitle>
          <DialogDescription>
            {returning ? `Ce que ${picked?.display_name || "la personne"} rend de l'avance non dépensée${held ? ` (au plus ${formatAmount(held, form.currency)})` : ""}.`
              : "L'argent remis avant la mission. Ses notes de frais approuvées s'en déduisent : rien n'est remboursé deux fois."}
            {mission && <> Mission {mission.reference}.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="advance-person">Personne</Label>
            {picked ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                <span className="truncate" data-testid="advance-person-picked">{picked.display_name}</span>
                {!person && <Button variant="ghost" size="sm" onClick={() => { setPicked(null); setForm((f) => ({ ...f, collaborator_id: "" })); }}>Changer</Button>}
              </div>
            ) : (
              <>
                <Input id="advance-person" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un collaborateur"
                  className="text-base md:text-sm" data-testid="advance-person-search" />
                <ul className="max-h-40 overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-100">
                  {people.map((p) => (
                    <li key={p.id}>
                      <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`advance-person-${p.id}`}
                        onClick={() => { setPicked(p); setForm((f) => ({ ...f, collaborator_id: p.id })); }}>
                        {p.display_name} <span className="text-xs text-slate-500">{p.reference}</span>
                      </button>
                    </li>
                  ))}
                  {people.length === 0 && <li className="px-3 py-2 text-sm text-slate-500">Aucun collaborateur trouvé.</li>}
                </ul>
              </>
            )}
            {showErrors && !form.collaborator_id && <p className="text-xs text-red-700">Choisissez la personne.</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="advance-amount">Montant</Label>
              <Input id="advance-amount" inputMode="decimal" value={form.amount} onChange={set("amount")} className="text-base md:text-sm tabular-nums"
                data-testid="advance-amount" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="advance-currency">Devise</Label>
              <select id="advance-currency" value={form.currency} onChange={set("currency")} className={selectClass} disabled={!!fixedCurrency}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="advance-on">Date</Label>
              <Input id="advance-on" type="date" max={todayIso()} value={form.on} onChange={set("on")} className="text-base md:text-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="advance-method">Moyen</Label>
              <select id="advance-method" value={form.method} onChange={set("method")} className={selectClass}>
                {Object.entries(ADVANCE_METHODS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="advance-note">Note</Label>
            <Textarea id="advance-note" rows={2} maxLength={1000} value={form.note} onChange={set("note")} placeholder="Facultatif." className="text-base md:text-sm" />
          </div>
          {showErrors && problem && form.collaborator_id && <p className="text-sm text-red-700" data-testid="advance-problem">{problem}</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={saving}  data-testid="advance-save">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}{returning ? "Enregistrer la restitution" : "Remettre l'avance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ advance, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (advance) setReason(""); }, [advance]);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/advances/${advance.id}/cancel`, { expected_version: advance.version, reason: reason.trim() });
      toast.success("Ligne annulée");
      onCancelled();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!advance} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent data-testid="advance-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Annuler cette ligne ?</DialogTitle>
          <DialogDescription>Elle reste visible, barrée, avec son motif, et ne compte plus dans le solde de la personne.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="advance-cancel-reason">Motif *</Label>
          <Textarea id="advance-cancel-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="text-base md:text-sm"
            data-testid="advance-cancel-reason" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button onClick={confirm} disabled={busy || !reason.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="advance-cancel-confirm">Annuler la ligne</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdvanceLine({ a, onCancel }) {
  const cancelled = !!a.cancelled_at;
  return (
    <li className="flex items-center gap-3 px-4 py-3" data-testid={`advance-${a.id}`}>
      {a.kind === "given" ? <Banknote size={18} className="text-slate-400 shrink-0" /> : <Undo2 size={18} className="text-slate-400 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-medium truncate ${cancelled ? "line-through text-slate-400" : "text-slate-900"}`}>
          {ADVANCE_KINDS[a.kind]} · {a.payer_label}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {formatDay(a.on)} · {ADVANCE_METHODS[a.method] || a.method}
          {a.mission && <> · <Link to={`/missions/${a.mission.id}`} className="text-jamora hover:underline">{a.mission.reference}</Link></>}
          {cancelled && <> · Annulée : {a.cancel_reason}</>}
        </div>
      </div>
      <span className={`text-sm font-semibold tabular-nums ${cancelled ? "line-through text-slate-400" : "text-slate-900"}`}>
        {a.kind === "returned" ? "− " : ""}{formatAmount(a.amount, a.currency)}
      </span>
      {onCancel && !cancelled && <Button variant="ghost" size="sm" onClick={() => onCancel(a)} data-testid={`advance-cancel-${a.id}`}>Annuler</Button>}
    </li>
  );
}

// Finance › « Avances de mission »: who holds how much, what is still owed to them, every advance and restitution.
export default function AdvancesPanel({ startCreate = false }) {
  const [state, setState] = useState({ phase: "loading", data: null, error: "" });
  const [dialog, setDialog] = useState(null); // {kind, person?, held?, currency?}
  const [cancelling, setCancelling] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/advances");
      setState({ phase: "ready", data, error: "" });
    } catch (e) {
      setState({ phase: "error", data: null, error: apiError(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const data = state.data;
  useEffect(() => { if (startCreate && data?.can_record) setDialog({ kind: "given" }); }, [startCreate, data?.can_record]); // from the global search

  return (
    <div className="space-y-3" data-testid="advances-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-500">L'argent remis avant une mission. Les notes de frais approuvées s'en déduisent ; ce qui n'est pas dépensé est restitué.</p>
        {data?.can_record && (
          <Button onClick={() => setDialog({ kind: "given" })}  data-testid="advances-new"><Plus size={16} className="mr-1.5" />Remettre une avance</Button>
        )}
      </div>
      {state.phase === "loading" && <Skeleton className="h-24 w-full" />}
      {state.phase === "error" && <Card className="p-4 text-sm text-slate-600 flex items-center gap-2">{state.error}<Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button></Card>}
      {data && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2">
          <Banknote className="mx-auto text-slate-300" size={24} />
          <p className="text-sm text-slate-600">Aucune avance de mission pour le moment.</p>
        </Card>
      )}
      {data && data.people.length > 0 && (
        <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="advances-people">
          {data.people.map((p) => Object.entries(p.balance).map(([c, b]) => (
            <div key={`${p.collaborator_id}-${c}`} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3" data-testid={`advances-person-${p.collaborator_id}`}>
              <span className="min-w-0 flex-1 font-medium text-slate-900 truncate">{p.label} <span className="text-xs text-slate-500">{c}</span></span>
              <span className="text-sm"><span className="block text-xs text-slate-500">Avances</span><span className="tabular-nums">{formatAmount(b.advanced || "0", c)}</span></span>
              <span className="text-sm"><span className="block text-xs text-slate-500">Frais approuvés</span><span className="tabular-nums">{formatAmount(b.owed, c)}</span></span>
              {Number(b.to_return) > 0 ? (
                <span className="text-sm text-amber-900"><span className="block text-xs">À restituer</span><strong className="tabular-nums">{formatAmount(b.to_return, c)}</strong></span>
              ) : (
                <span className="text-sm"><span className="block text-xs text-slate-500">Reste dû</span><strong className="tabular-nums">{formatAmount(b.remaining, c)}</strong></span>
              )}
              {data.can_record && Number(b.to_return) > 0 && (
                <Button variant="outline" size="sm" data-testid={`advances-return-${p.collaborator_id}`}
                  onClick={() => setDialog({ kind: "returned", person: { id: p.collaborator_id, display_name: p.label }, held: b.to_return, currency: c })}>
                  <Undo2 size={14} className="mr-1.5" />Restitution
                </Button>
              )}
            </div>
          )))}
        </Card>
      )}
      {data && data.items.length > 0 && (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-slate-100" data-testid="advances-list">
            {data.items.map((a) => <AdvanceLine key={a.id} a={a} onCancel={data.can_record ? setCancelling : null} />)}
          </ul>
        </Card>
      )}
      <AdvanceDialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }} onRecorded={load} kind={dialog?.kind} person={dialog?.person}
        held={dialog?.held} currency={dialog?.currency} />
      <CancelDialog advance={cancelling} onClose={() => setCancelling(null)} onCancelled={load} />
    </div>
  );
}
